// src/startup/deeplink.ts
console.log('🟢 deeplink.ts module LOADED');

import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { supabase, storageReady } from '@/integrations/supabase/client';
import { Capacitor } from '@capacitor/core';

const NATIVE_REDIRECT = 'clan://auth/callback';
const STORAGE_PREFIX = 'sb-wqagrmomwzikqpxwiskl-auth-token'; // ton project ref

let lastHandledUrl: string | null = null;
let lastHandledAt = 0;

/* ----------------------- Helpers ----------------------- */

function parseFragment(url: string) {
  const hash = url.split('#')[1] || '';
  const params = new URLSearchParams(hash);
  return {
    access_token: params.get('access_token') || undefined,
    refresh_token: params.get('refresh_token') || undefined,
  };
}

function parseQueryCode(url: string) {
  try {
    const stdUrl = url.startsWith('clan://') ? url.replace('clan://', 'https://') : url;
    const u = new URL(stdUrl);
    return {
      code: u.searchParams.get('code') || undefined,
      state: u.searchParams.get('state') || undefined,
    };
  } catch {
    return { code: undefined, state: undefined };
  }
}

function isAuthCallback(url: string) {
  // en natif, on ne devrait recevoir que le scheme custom
  return url.startsWith(NATIVE_REDIRECT);
}

/* ------------------------ Core ------------------------- */

async function handleAuthDeepLink(url?: string) {
  if (!url) return;
  console.log('🔗 [DeepLink] Received URL:', url);

  if (!isAuthCallback(url)) {
    console.log('⏭️ [DeepLink] Not an auth callback, ignoring');
    return;
  }

  if (lastHandledUrl === url) {
    console.log('⏭️ [DeepLink] Duplicate URL, skipping');
    return;
  }
  lastHandledUrl = url;

  try {
    // 0) S’assurer que le storage (et polyfill sessionStorage) est prêt
    try {
      console.log('🧰 Awaiting storageReady before PKCE exchange…');
      await storageReady;
    } catch {
      console.warn('⚠️ storageReady failed (continuing)');
    }

    // 1) Extraire le code d’autorisation (depuis clan:// on remappe en https:// juste pour parser)
    const parserUrl = url.startsWith('clan://') ? url.replace('clan://', 'https://') : url;
    const u = new URL(parserUrl);
    const code = u.searchParams.get('code') || undefined;
    const state = u.searchParams.get('state') || undefined;

    // 2) Tokens éventuels dans le fragment (fallback)
    const { access_token, refresh_token } = parseFragment(url);

    console.log('🔍 URL parts →', {
      hasCode: !!code, hasState: !!state, hasAccess: !!access_token, hasRefresh: !!refresh_token,
    });

    // 3) Debug PKCE keys (flow-state & code-verifier) dans le storage
    try {
      // @ts-ignore
      const st = supabase.auth.storage;
      const flowState = st?.getItem?.(`sb-wqagrmomwzikqpxwiskl-auth-token-flow-state`);
      const codeVerifier = st?.getItem?.(`sb-wqagrmomwzikqpxwiskl-auth-token-code-verifier`);
      console.log('🔎 PKCE keys → flow-state:', flowState ? 'FOUND' : 'NOT FOUND',
                  'code-verifier:', codeVerifier ? 'FOUND' : 'NOT FOUND');
    } catch {}

    let ok = false;

    // 4) Chemin NORMAL : échanger **le code** (pas l’URL)
    if (code) {
      try {
        const { data, error } = await supabase.auth.exchangeCodeForSession(code);
        console.log('🔐 exchangeCodeForSession(code) →', {
          hasSession: !!data?.session, userId: data?.user?.id, err: error?.message,
        });
        ok = !!data?.session && !error;

        if (!ok && error && typeof error.message === 'string' &&
            (error.message.includes('flow') || error.message.toLowerCase().includes('invalid'))) {
          console.warn('⚠️ PKCE(code) error, retrying after 250ms…');
          await new Promise(r => setTimeout(r, 250));
          const retry = await supabase.auth.exchangeCodeForSession(code);
          console.log('🔁 retry(code) →', {
            hasSession: !!retry.data?.session, userId: retry.data?.user?.id, err: retry.error?.message,
          });
          ok = !!retry.data?.session && !retry.error;
        }
      } catch (e) {
        console.warn('⚠️ PKCE(code) exception:', e);
      }
    } else {
      console.error('❌ No authorization code found in callback URL.');
    }

    // 5) Dernier recours : si des tokens sont présents dans le #fragment
    if (!ok && access_token && refresh_token) {
      try {
        const { data, error } = await supabase.auth.setSession({ access_token, refresh_token });
        console.log('🔐 setSession(fragment tokens) →', {
          hasSession: !!data?.session, userId: data?.user?.id, err: error?.message,
        });
        ok = !!data?.session && !error;
      } catch (e) {
        console.error('setSession error:', e);
      }
    }

    // 6) Vérif et dispatch navigation
    await new Promise(r => setTimeout(r, 300));
    const { data: verify } = await supabase.auth.getSession();
    console.log('✅ Session verification:', { has: !!verify?.session, userId: verify?.session?.user?.id });

    if ((ok || verify?.session?.user) && !(window as any).__oauthDispatched) {
      (window as any).__oauthDispatched = true;
      window.dispatchEvent(new CustomEvent('oauth:signedin', {
        detail: { userId: verify?.session?.user?.id },
      }));
    }

    if (!verify?.session) console.error('❌ Session not persisted after deep link!');
  } catch (err) {
    console.error('❌ Deep link handling failed:', err);
  } finally {
    try {
      console.log('🚪 Closing browser…');
      await Browser.close();
    } catch {}
  }
}


/* -------------------- Boot (native) -------------------- */

if (Capacitor.isNativePlatform()) {
  console.log('📱 Initializing deep link handler (native platform)');

  App.addListener('appUrlOpen', async ({ url }) => {
    console.log('🔙 appUrlOpen event:', url);
    await handleAuthDeepLink(url);
  });

  App.getLaunchUrl().then(async (res) => {
    if (res?.url) {
      console.log('🚀 getLaunchUrl (cold start):', res.url);
      await handleAuthDeepLink(res.url);
    }
  });
}
